/*
 * @author Lemuel Raganas <lemuel.raganas@mediacorp.com.sg>
 **/

import DataApiLoader from "@/libs/DataApiLoader";
import MainStub from "@/libs/data-api-preloader/MainStub";
import DataRepoOption from "@/libs/world-cup/DataRepoOption";
import OnPreload from "@/libs/data-api-preloader/OnPreload";
import OnPreloadHandler from "@/libs/data-api-preloader/OnPreloadHandler";
import OnPreloadDoneHandler from "@/libs/data-api-preloader/OnPreloadDoneHandler";

export default class DataApiPreloader extends DataApiLoader {
  private dataRepoOptions: DataRepoOption[];
  private preloadedRepos: string[];
  private onPreloadHandler: OnPreloadHandler;
  private onPreloadDoneHandler: OnPreloadDoneHandler;

  constructor(stub?: MainStub) {
    super(stub);

    this.onPreloadHandler = new OnPreloadHandler();
    this.onPreloadDoneHandler = new OnPreloadDoneHandler();
    this.dataRepoOptions = stub.dataRepoOptions || [];
    this.preloadedRepos = stub.preloadedRepos || [];
    this.onPreloadHandler.add(stub.onPreload);
    this.onPreloadDoneHandler.add(stub.onPreloadDone);

    // Default handler for data ready
    this.onReady(this.onPreloaderDataReady);
  }

  public setDataRepoOptions(dataRepoOptions: DataRepoOption[]) {
    this.dataRepoOptions = dataRepoOptions;
    return this;
  }

  public setPreloadedRepos(preloadedRepos: string[]) {
    this.preloadedRepos = preloadedRepos;
    return this;
  }

  public onPreload(handler: OnPreload) {
    this.onPreloadHandler.add(handler);
    return this;
  }

  public preload() {
    const repo = this.getUnpreloadedRepos();

    if (repo === undefined) {
      this.onPreloadDoneHandler.handle(this.preloadedRepos);
      return;
    }

    if (this.onPreloadHandler !== undefined) {
      this.onPreloadHandler.handle(repo.name, this);
    }

    // Now mark this repo as pre-loaded but prevent any duplicates
    if (!this.preloadedRepos.includes(repo.name)) {
      this.preloadedRepos.push(repo.name);
    }

    // Load actual data
    this.load(repo.name, repo.option);
  }

  /**
   * Default onReady method for this implementation.
   *
   * @param name
   * @param data
   * @param dataApiPreloader
   */
  private onPreloaderDataReady(
    name: string,
    data?: any,
    dataApiPreloader?: DataApiPreloader
  ) {
    // Begin another preload until no more data repo to preload
    dataApiPreloader.preload();
  }

  private getUnpreloadedRepos(): DataRepoOption {
    const dataRepoOption = this.dataRepoOptions.find((option) => {
      if (!this.dataApi.hasApi(option.name)) {
        return false;
      }

      if (!option.preload) {
        // This here will only try to load endpoints marked as preload
        return false;
      }

      if (this.preloadedRepos.includes(option.name)) {
        return false;
      }

      return true;
    });

    return dataRepoOption;
  }
}
